// Reusable pre-start device chooser: pick microphone + speaker, with a
// live mic-level meter and a speaker test tone. Persists to the shared
// DevicePrefs (lib/devices), so it stays in sync with Settings → Devices
// and is honored by the recorder + hosted calls. Used by the solo
// recorder pre-start and the hosted-call lobby.

import { html, LitElement, nothing } from "lit";
import {
  applySpeaker,
  type DeviceList,
  type DevicePrefs,
  listDevices,
  loadDevicePrefs,
  micConstraints,
  saveDevicePrefs,
} from "../lib/devices";

export class MmDevicePicker extends LitElement {
  private prefs: DevicePrefs = loadDevicePrefs();
  private devices: DeviceList = { mics: [], speakers: [], cameras: [] };
  private micLevel = 0;
  private notice = "";

  private stream: MediaStream | null = null;
  private audioCtx: AudioContext | null = null;
  private raf = 0;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this.startPreview();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopPreview();
  }

  /** Open the mic to drive the level meter and reveal device labels. */
  private async startPreview(): Promise<void> {
    this.stopPreview();
    this.notice = "";
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: micConstraints(undefined, this.prefs),
      });
      this.startMeter(this.stream);
    } catch (e) {
      this.notice =
        e instanceof Error ? e.message : "could not open the selected microphone";
    }
    // Labels are only populated once permission is granted.
    try {
      this.devices = await listDevices();
    } catch {
      /* enumeration may fail pre-permission — leave the list empty */
    }
    this.requestUpdate();
  }

  private startMeter(stream: MediaStream): void {
    if (stream.getAudioTracks().length === 0) return;
    this.audioCtx = new AudioContext();
    const src = this.audioCtx.createMediaStreamSource(stream);
    const analyser = this.audioCtx.createAnalyser();
    analyser.fftSize = 512;
    src.connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const tick = (): void => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) sum += v * v;
      this.micLevel = Math.min(1, Math.sqrt(sum / buf.length) * 6);
      this.requestUpdate();
      this.raf = requestAnimationFrame(tick);
    };
    tick();
  }

  private stopPreview(): void {
    this.micLevel = 0;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    if (this.audioCtx && this.audioCtx.state !== "closed") void this.audioCtx.close();
    this.audioCtx = null;
  }

  private onMic(id: string): void {
    this.prefs = saveDevicePrefs({ micId: id });
    void this.startPreview(); // reopen on the chosen mic
  }

  private onSpeaker(id: string): void {
    this.prefs = saveDevicePrefs({ speakerId: id });
    this.requestUpdate();
  }

  /** Short tone through the selected output to confirm it. */
  private async testSpeaker(): Promise<void> {
    try {
      const ctx = new AudioContext();
      const dest = ctx.createMediaStreamDestination();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440;
      gain.gain.value = 0.15;
      osc.connect(gain).connect(dest);
      const audio = new Audio();
      audio.srcObject = dest.stream;
      await applySpeaker(audio, this.prefs.speakerId);
      await audio.play();
      osc.start();
      setTimeout(() => {
        osc.stop();
        audio.pause();
        void ctx.close();
      }, 600);
    } catch {
      this.notice = "could not play a test tone on the selected speaker";
      this.requestUpdate();
    }
  }

  private select(
    label: string,
    kind: "mics" | "speakers",
    value: string,
    fallback: string,
    onChange: (id: string) => void,
  ) {
    return html`
      <label class="device-row">
        <span class="device-row-label">${label}</span>
        <select
          class="device-select"
          @change=${(e: Event) => onChange((e.target as HTMLSelectElement).value)}
        >
          <option value="">${fallback}</option>
          ${this.devices[kind].map(
            (d) =>
              html`<option value=${d.deviceId} ?selected=${d.deviceId === value}>
                ${d.label || `${label} ${d.deviceId.slice(0, 6)}`}
              </option>`,
          )}
        </select>
      </label>
    `;
  }

  override render() {
    const speakerSelectable =
      typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId === "function";
    const pct = Math.round(this.micLevel * 100);
    return html`
      <div class="device-picker">
        ${this.select("Microphone", "mics", this.prefs.micId, "Default microphone", (id) =>
          this.onMic(id),
        )}
        ${this.select(
          "Speaker",
          "speakers",
          this.prefs.speakerId,
          speakerSelectable ? "Default speaker" : "Default speaker (system-controlled)",
          (id) => this.onSpeaker(id),
        )}
        <div class="device-meter">
          <span class="device-row-label">Mic level</span>
          <div class="level-meter">
            <div class="level-meter-fill ${pct > 85 ? "hot" : ""}" style="width: ${pct}%"></div>
          </div>
          <button
            class="ghost-button"
            ?disabled=${!speakerSelectable}
            title=${speakerSelectable
              ? "Play a test tone on the selected speaker"
              : "This browser controls audio output at the system level"}
            @click=${this.testSpeaker}
          >
            🔊 Test
          </button>
        </div>
        ${this.notice
          ? html`<div class="message-card message-card-warn"><p>${this.notice}</p></div>`
          : nothing}
      </div>
    `;
  }
}

customElements.define("mm-device-picker", MmDevicePicker);
